import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Info, TriangleAlert } from 'lucide-react';
import clsx from 'clsx';
import type { DocBlock } from '@/data/docs/types';

const TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\)|`[^`]+`)/;
const LINK = /^\[([^\]]+)\]\(([^)\s]+)\)$/;

const LINK_CLASS =
  'text-blue-700 underline decoration-blue-700/30 underline-offset-[3px] transition-colors hover:decoration-blue-700';

/** Надпись или путь в интерфейсе. Путь «Раздел → Кнопка» набирается плашкой с шевронами. */
function UiLabel({ label }: { label: string }) {
  const parts = label.split(' → ');
  if (parts.length === 1) return <b className="font-semibold text-ink">{label}</b>;
  return (
    <span className="rounded-md bg-ink/[0.055] px-1.5 py-[3px] font-medium text-ink [-webkit-box-decoration-break:clone] [box-decoration-break:clone]">
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <>
              <span className="sr-only"> → </span>
              <ChevronRight
                aria-hidden
                strokeWidth={2.25}
                className="mx-[3px] inline-block h-[0.95em] w-[0.95em] -translate-y-[0.06em] text-ink/40"
              />
            </>
          )}
          {part}
        </Fragment>
      ))}
    </span>
  );
}

function DocLink({ href, children }: { href: string; children: ReactNode }) {
  if (href.startsWith('/') || href.startsWith('#')) {
    return (
      <Link to={href} className={LINK_CLASS}>
        {children}
      </Link>
    );
  }
  const external = href.startsWith('http');
  return (
    <a
      href={href}
      className={LINK_CLASS}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

export function Inline({ text }: { text: string }) {
  const parts = text.split(TOKEN);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return part;
        if (part.startsWith('**')) return <UiLabel key={i} label={part.slice(2, -2)} />;
        if (part.startsWith('`')) {
          return (
            <code key={i} className="rounded bg-ink/[0.055] px-1 py-0.5 font-mono text-[0.86em] text-ink">
              {part.slice(1, -1)}
            </code>
          );
        }
        const link = LINK.exec(part);
        return link ? (
          <DocLink key={i} href={link[2]}>
            {link[1]}
          </DocLink>
        ) : (
          part
        );
      })}
    </>
  );
}

const NOTE_TONES = {
  info: { Icon: Info, label: 'Примечание', box: 'bg-blue-50', icon: 'text-blue-700' },
  warning: { Icon: TriangleAlert, label: 'Важно', box: 'bg-orange-50', icon: 'text-orange-700' },
} as const;

export function DocBlockView({ block }: { block: DocBlock }) {
  switch (block.kind) {
    case 'p':
      return (
        <p className="mt-4 text-[16px] leading-[1.7] text-ink/80">
          <Inline text={block.text} />
        </p>
      );

    case 'h3':
      return <h3 className="mt-9 text-[18px] font-medium leading-snug tracking-[-0.01em] text-ink">{block.text}</h3>;

    case 'steps':
      return (
        <ol className="mt-5 space-y-3.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3.5">
              <span
                aria-hidden
                className="mt-[2px] grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink/[0.06] text-[12.5px] font-semibold tabular-nums text-ink/75"
              >
                {i + 1}
              </span>
              <span className="min-w-0 text-[16px] leading-[1.65] text-ink/80">
                <Inline text={item} />
              </span>
            </li>
          ))}
        </ol>
      );

    case 'list':
      return (
        <ul className="mt-4 space-y-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-[16px] leading-[1.65] text-ink/80">
              <span aria-hidden className="mt-[0.68em] h-1.5 w-1.5 shrink-0 rounded-full bg-ink/30" />
              <span className="min-w-0">
                <Inline text={item} />
              </span>
            </li>
          ))}
        </ul>
      );

    case 'note': {
      const tone = NOTE_TONES[block.tone];
      return (
        <div className={clsx('mt-5 flex gap-3 rounded-xl px-4 py-3.5', tone.box)}>
          <tone.Icon aria-hidden size={18} strokeWidth={2} className={clsx('mt-[3px] shrink-0', tone.icon)} />
          <p className="min-w-0 text-[15px] leading-[1.65] text-ink/85">
            <span className="sr-only">{tone.label}: </span>
            <Inline text={block.text} />
          </p>
        </div>
      );
    }

    case 'table':
      return (
        <div className="mt-5 overflow-x-auto rounded-xl border border-ink/[0.09]">
          <table className={clsx('w-full border-collapse text-left text-[14.5px]', block.head.length > 2 && 'min-w-[34rem]')}>
            <thead>
              <tr className="bg-ink/[0.03]">
                {block.head.map((cell, i) => (
                  <th key={i} scope="col" className="px-4 py-2.5 text-[13px] font-medium text-ink/65">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-t border-ink/[0.08]">
                  {row.map((cell, c) =>
                    c === 0 ? (
                      <th key={c} scope="row" className="px-4 py-3 align-top font-medium text-ink">
                        <Inline text={cell} />
                      </th>
                    ) : (
                      <td key={c} className="px-4 py-3 align-top leading-relaxed text-ink/75">
                        <Inline text={cell} />
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
